import clsx from 'clsx';
import React, { useMemo } from 'react';
import { Image } from '~/shared/components/Image';
import {
    ColorSwatch,
    TProductDetails,
} from '~/shared/components/ProductCard/ProductCard.definition';
import Typography from '~/shared/components/Typography/Typography';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductAttributes.module.scss';
import { replaceProductHyphensWithSlashes } from '~/lib/salesforce/utilities/formatters';

interface ProductAttributesProps {
    id: string;
    availableSwatches: ColorSwatch[];
    hasOtherVariants: boolean;
    attributesSummary: string | undefined;
}

type Props = ProductAttributesProps &
    React.HTMLAttributes<HTMLDivElement> & {
        colorsDisplayLimit?: number;
    };

export const extractProductAttributes = (product: TProductDetails): ProductAttributesProps => ({
    id: product.id,
    availableSwatches: product.cust_swatchimages_siblings || [],
    hasOtherVariants:
        product.cust_hasSiblings || (product.variants && product.variants.length > 1) || false,
    attributesSummary: product.label,
});

export const ProductAttributes: React.FC<Props> = ({
    className,
    id,
    availableSwatches,
    hasOtherVariants,
    attributesSummary,
    colorsDisplayLimit = 3,
    ...rest
}) => {
    const { translate } = useTranslation();

    const swatchesToShow = useMemo(() => availableSwatches.slice(0, colorsDisplayLimit), [id]);

    // We will only show the color variants if there are other color variants.
    const hasOtherColorVariants = availableSwatches.length >= 2;

    // Formatted swatches hidden because of display limit.
    const truncatedSwatches = useMemo(
        () => swatchesToShow.slice(colorsDisplayLimit - 1).map((swatch) => swatch.title),
        [id],
    );

    return (
        <div className={clsx(styles.attributes, className)} {...rest}>
            {attributesSummary && (
                <p className={styles.summary}>
                    {replaceProductHyphensWithSlashes(attributesSummary)}
                </p>
            )}

            {hasOtherColorVariants || hasOtherVariants ? (
                <div className={styles.variants}>
                    <span className={styles.label}>{translate('plp.variantsLabel')}</span>

                    {hasOtherColorVariants && (
                        <div className={styles.colors}>
                            {swatchesToShow.map((swatch) => (
                                <Image
                                    key={swatch.color}
                                    className={clsx(styles.color, {
                                        // We will offset the color of white variants on light surfaces.
                                        [styles.inverseBorder]: swatch.title === 'Hvid',
                                    })}
                                    src={swatch.url}
                                    alt={swatch.alt}
                                    title={swatch.title}
                                    layout="fixed"
                                    width={12}
                                    height={12}
                                />
                            ))}

                            {!!truncatedSwatches.length && (
                                <Typography
                                    className={styles.overflowText}
                                    variant="paragraph-XS"
                                    as="span"
                                    title={truncatedSwatches.join(', ')}
                                >
                                    +{truncatedSwatches.length}
                                </Typography>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                // Used to retain component height when there are no variants.
                <div className={styles.variantsPlaceholder}></div>
            )}
        </div>
    );
};

export default ProductAttributes;
